import { Button, Form, Input, Modal, Switch } from 'antd';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
} from '@/modules/email-templates/emailTemplatesApi';
import {
  EMAIL_SIGNATURE_KEY,
  friendlyToStorageTemplate,
  getEmailFieldLabel,
  storageToFriendlyTemplate,
  toFriendlyFieldToken,
  type EmailTemplate,
} from '@/types/emailTemplate';
import { resolveMutationResult } from '@/utils/apiResponse';
import styles from './EmailTemplateFormModal.module.scss';

interface EmailTemplateFormModalProps {
  open: boolean;
  template?: EmailTemplate | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  subject: string;
  htmlBody: string;
  includeSignature: boolean;
  isActive: boolean;
}

function appendToken(current: string, token: string): string {
  if (!current) return token;
  const needsSpace = !/\s$/.test(current);
  return `${current}${needsSpace ? ' ' : ''}${token}`;
}

export function EmailTemplateFormModal({ open, template, onClose }: EmailTemplateFormModalProps) {
  const notify = useNotify();
  const isEdit = Boolean(template);
  const isSignature = template?.key === EMAIL_SIGNATURE_KEY;
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [createTemplate, { isLoading: isCreating }] = useCreateEmailTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateEmailTemplateMutation();
  const isLoading = isCreating || isUpdating;
  useGlobalLoader(isLoading);

  const fields = template?.availableVariables?.length
    ? template.availableVariables
    : ['recipientName', 'email', 'phone', 'siteName', 'supportEmail', 'orderNumber', 'customerName'];

  const { control, handleSubmit, reset, setValue, getValues } = useForm<FormValues>({
    defaultValues: {
      name: '',
      subject: '',
      htmlBody: '',
      includeSignature: true,
      isActive: true,
    },
  });

  useEffect(() => {
    if (!open) return;

    if (template) {
      const keys = template.availableVariables ?? [];
      reset({
        name: template.name,
        subject: storageToFriendlyTemplate(template.subject, keys),
        htmlBody: storageToFriendlyTemplate(template.htmlBody, keys),
        includeSignature: Boolean(template.includeSignature),
        isActive: template.isActive,
      });
    } else {
      reset({
        name: '',
        subject: '',
        htmlBody: '<p>Dear [Customer name],</p><p>Write your email message here.</p>',
        includeSignature: true,
        isActive: true,
      });
    }
    setEditorEpoch(0);
  }, [open, template, reset]);

  const insertIntoSubject = (variable: string) => {
    setValue('subject', appendToken(getValues('subject') || '', toFriendlyFieldToken(variable)), {
      shouldDirty: true,
    });
  };

  const insertIntoBody = (variable: string) => {
    setValue('htmlBody', appendToken(getValues('htmlBody') || '', toFriendlyFieldToken(variable)), {
      shouldDirty: true,
    });
    setEditorEpoch((value) => value + 1);
  };

  const onSubmit = async (values: FormValues) => {
    const keys = fields;
    const subject = friendlyToStorageTemplate(values.subject.trim(), keys);
    const htmlBody = friendlyToStorageTemplate(values.htmlBody, keys);

    if (isEdit && template) {
      const result = await resolveMutationResult(
        updateTemplate({
          key: template.key,
          body: isSignature
            ? {
                htmlBody,
                textBody: '',
                isActive: values.isActive,
              }
            : {
                name: values.name.trim(),
                subject,
                htmlBody,
                textBody: '',
                includeSignature: values.includeSignature,
                isActive: values.isActive,
              },
        }),
      );

      if (!result.ok) {
        notify.error(result.message);
        return;
      }

      notify.success(
        result.message ?? (isSignature ? 'Default signature updated.' : 'Email template updated.'),
      );
      onClose();
      return;
    }

    const result = await resolveMutationResult(
      createTemplate({
        name: values.name.trim(),
        subject,
        htmlBody,
        audience: 'custom',
        includeSignature: values.includeSignature,
        isActive: values.isActive,
      }),
    );

    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    notify.success(result.message ?? 'Email template created.');
    onClose();
  };

  const modalTitle = isSignature
    ? 'Update Default Signature'
    : isEdit
      ? 'Update Email Template'
      : 'Create Email Template';

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={720}
      centered
    >
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        {isSignature ? (
          <p className={styles.fieldHint}>
            This footer is added to every template that has Attach default signature turned on.
          </p>
        ) : (
          <Form.Item label="Title" required>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Title is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    placeholder="e.g. Welcome Email"
                    disabled={Boolean(template?.isSystem)}
                  />
                  {fieldState.error ? (
                    <span className={styles.fieldError}>{fieldState.error.message}</span>
                  ) : null}
                </>
              )}
            />
          </Form.Item>
        )}

        {isSignature ? null : (
          <Form.Item label="Subject" required>
            <Controller
              name="subject"
              control={control}
              rules={{ required: isSignature ? false : 'Subject is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Input {...field} placeholder="Email subject line" />
                  {fieldState.error ? (
                    <span className={styles.fieldError}>{fieldState.error.message}</span>
                  ) : null}
                </>
              )}
            />
            <p className={styles.fieldHint}>Click a detail to add it to the subject:</p>
            <div className={styles.fieldButtons}>
              {fields.map((variable) => (
                <button
                  key={`subject-${variable}`}
                  type="button"
                  className={styles.fieldChip}
                  onClick={() => insertIntoSubject(variable)}
                >
                  {getEmailFieldLabel(variable)}
                </button>
              ))}
            </div>
          </Form.Item>
        )}

        <Form.Item label={isSignature ? 'Signature' : 'Message'} required>
          <p className={styles.fieldHint}>Click a detail to add it to the {isSignature ? 'signature' : 'message'}:</p>
          <div className={styles.fieldButtons}>
            {fields.map((variable) => (
              <button
                key={`body-${variable}`}
                type="button"
                className={styles.fieldChip}
                onClick={() => insertIntoBody(variable)}
              >
                {getEmailFieldLabel(variable)}
              </button>
            ))}
          </div>
          <Controller
            name="htmlBody"
            control={control}
            rules={{ required: isSignature ? 'Signature is required' : 'Message is required' }}
            render={({ field, fieldState }) => (
              <>
                <RichTextEditor
                  key={`editor-${template?.key ?? 'new'}-${editorEpoch}`}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={isSignature ? 'Write the default email signature…' : 'Write the email message…'}
                  height={280}
                  onUploadError={(message) => notify.error(message)}
                />
                {fieldState.error ? (
                  <span className={styles.fieldError}>{fieldState.error.message}</span>
                ) : null}
              </>
            )}
          />
        </Form.Item>

        {isSignature ? null : (
          <Form.Item label="Attach default signature" className={styles.statusField}>
            <Controller
              name="includeSignature"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={field.onChange}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
              )}
            />
            <p className={styles.fieldHint}>
              When on, the default signature is added at the bottom of this email when it is sent.
            </p>
          </Form.Item>
        )}

        <Form.Item label="Status" className={styles.statusField}>
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Switch
                checked={field.value}
                onChange={field.onChange}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            )}
          />
        </Form.Item>

        <div className={styles.footer}>
          <Button onClick={onClose}>Close</Button>
          <Button type="primary" htmlType="submit" loading={isLoading} danger={false}>
            {isSignature ? 'Save Signature' : isEdit ? 'Save Changes' : 'Create Template'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
